import { FC, useEffect, useState } from 'react';

import { css } from '@emotion/react';
import { VisuallyHidden } from 'react-aria-components';

import {
  minimumPasswordValidationMessage,
  mustHaveLetterValidationMessage,
  repeatedCharacterValidationMessage,
} from '../passwordValidation';
import PasswordRequirement from './PasswordRequirement';

const requirementsStyle = css`
  li {
    display: flex;
    flex-direction: row;
  }
`;

export const passwordRequirementTestIds = {
  minimumLengthPassword: 'passwordRequirementMinimumLength',
  hasLetter: 'passwordRequirementValidLetter',
  repeatedCharacter: 'passwordRequirementRepeatedCharacters',
};

/** Not tied to the input via aria-describedby: marketing-ui's TextInput overwrites it. */
export const passwordRequirementsListLabel = 'Password requirements';

export const allRequirementsMetMessage = 'All password requirements met.';

export const requirementsMetMessage = (met: number, total: number) =>
  `${met} of ${total} password requirements met.`;

/** For an error with no row (the 512-character maximum); "All met" would be false. */
export const unlistedRequirementFailedMessage =
  'Password not accepted: it fails a requirement that is not listed.';

/** Debounce so the live region doesn't fire on every keystroke. */
export const announcementDebounceMs = 1000;

type PasswordRequirementsProps = {
  password: string;
  passwordErrors?: string;
};

const PasswordRequirements: FC<PasswordRequirementsProps> = ({
  password,
  passwordErrors,
}) => {
  const [validMinimumPasswordLength, setValidMinimumPasswordLength] =
    useState(false);
  const [validLetterValidation, setValidLetterValidation] = useState(false);
  const [validRepeatedCharacters, setValidRepeatedCharacters] = useState(false);

  useEffect(() => {
    if (password.length < 1) {
      setValidLetterValidation(false);
      setValidMinimumPasswordLength(false);
      setValidRepeatedCharacters(false);
      return;
    }

    setValidLetterValidation(
      !passwordErrors?.includes(mustHaveLetterValidationMessage)
    );
    setValidMinimumPasswordLength(
      !passwordErrors?.includes(minimumPasswordValidationMessage)
    );
    setValidRepeatedCharacters(
      !passwordErrors?.includes(repeatedCharacterValidationMessage)
    );
  }, [
    password,
    passwordErrors,
    setValidLetterValidation,
    setValidRepeatedCharacters,
    setValidMinimumPasswordLength,
  ]);

  const requirements: {
    testId: string;
    requirementIsSatisfied: boolean;
    requirementMessage: string;
  }[] = [
    {
      testId: passwordRequirementTestIds.minimumLengthPassword,
      requirementIsSatisfied: validMinimumPasswordLength,
      requirementMessage: minimumPasswordValidationMessage,
    },
    {
      testId: passwordRequirementTestIds.hasLetter,
      requirementIsSatisfied: validLetterValidation,
      requirementMessage: mustHaveLetterValidationMessage,
    },
    {
      testId: passwordRequirementTestIds.repeatedCharacter,
      requirementIsSatisfied: validRepeatedCharacters,
      requirementMessage: repeatedCharacterValidationMessage,
    },
  ];

  const satisfiedCount = requirements.filter(
    ({ requirementIsSatisfied }) => requirementIsSatisfied
  ).length;

  // Gate the all-clear on passwordErrors too: an error with no row leaves every row "met".
  const hasValidationErrors = Boolean(passwordErrors?.trim());
  const summary =
    password.length < 1
      ? ''
      : satisfiedCount < requirements.length
      ? requirementsMetMessage(satisfiedCount, requirements.length)
      : hasValidationErrors
      ? unlistedRequirementFailedMessage
      : allRequirementsMetMessage;

  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    // `password` restarts the timer on each keystroke.
    const timer = setTimeout(
      () => setAnnouncement(summary),
      announcementDebounceMs
    );

    return () => clearTimeout(timer);
  }, [password, summary]);

  return (
    <div css={requirementsStyle}>
      <ul aria-label={passwordRequirementsListLabel}>
        {requirements.map(
          ({ requirementIsSatisfied, requirementMessage, testId }, i) => (
            <li key={i}>
              <PasswordRequirement
                testId={testId}
                requirementIsSatisfied={requirementIsSatisfied}
              >
                {requirementMessage}
              </PasswordRequirement>
            </li>
          )
        )}
      </ul>
      {/* Outside the list, and always mounted, so AT observes it before the text changes. */}
      <VisuallyHidden elementType="div" role="status" aria-live="polite">
        {announcement}
      </VisuallyHidden>
    </div>
  );
};

export default PasswordRequirements;
